<template>
  <div class="flex flex-col gap-y-1">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="flex items-center gap-x-2 text-brand-black-secondary"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex">
        <VDatePicker
          :required="isRequired"
          v-model="refDateInput"
          :popover="false"
          @popover-did-hide="handleWrapBlur"
          mode="time"
          is24hr
          hide-time-header
          :masks="masks"
          :rules="xRules"
        >
          <template #default="{ togglePopover, inputValue, inputEvents }">
            <div class="w-full relative">
              <input
                :class="[{ 'border-s-4 border-s-primary': isRequired }]"
                placeholder="HH:mm"
                type="text"
                class="w-full ps-3 py-1 border border-brand-grayblue-300 outline-none focus:border-primary placeholder:text-xs"
                :value="inputValue"
                v-on="inputEvents"
                @blur="handleWrapBlur"
              />

              <div
                class="border-x border-x-gray-300 absolute top-0 end-0 h-full flex items-center gap-x-4 px-3"
                @click.prevent="togglePopover"
              >
                <button>
                  <FontAwesomeIcon :icon="faClock" class="text-brand-black-secondary" />
                </button>
              </div>
            </div>
          </template>
        </VDatePicker>
      </div>
    </div>
    <div v-if="hasError" class="w-full flex flex-col md:flex-row gap-x-4 gap-y-1 md:items-center">
      <div v-if="showErrorSpacer" class="w-full md:w-1/3"></div>
      <span class="w-full pt-[2px] text-brand-red ltr:font-sans text-xs">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import useRuleHandler from '@/composables/useRuleHandler'
import useFormatDate from '@/composables/useFormatDate'
import useDatePickerAction from '@/composables/actions/useDatePickerAction'

import { DateTime } from 'luxon'

import { useField } from 'vee-validate'
import { computed, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { faClock } from '@fortawesome/free-regular-svg-icons'
import HintTippy from '@/components/Form/HintTyppy.vue'

const { formateDateFromHHMM } = useFormatDate()
const defaultDateFormat = 'HH:mm'
const i18n = useI18n()

const props = defineProps({
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  defaultValue: Date,
  placeholder: String,
  hint: String,
  isDisabled: Boolean,
  dependentOn: String,
  rules: Object,
  dateFormat: {
    type: String,
    default: defaultDateFormat
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  showErrorSpacer: {
    type: Boolean,
    default: () => true
  },
  onBlur: Object,
  web: Object
})

const formattedDate = ref()
const fieldName = toRef(props, 'fieldName')
// v-datepicker issue: pass "" can't pick any date.
const bindObj = computed(() => {
  const res = {}
  return res
})

const hasError = computed(() => errorMessage.value && meta.touched)

const masks = ref({
  input: 'HH:mm'
})

const {
  errorMessage,
  handleChange,
  handleBlur,
  meta,
  value: dateInputValue
} = useField(fieldName, (value) => useRuleHandler(value, props.rules), {
  initialValue: props.defaultValue
})

const handleWrapBlur = () => {
  handleBlur()
  if (props.onBlur && props.onBlur.action) {
    useDatePickerAction()[props.onBlur.action](formattedDate.value)
  }
}

dateInputValue.value = props.defaultValue
  ? props.defaultValue
  : DateTime.fromJSDate(new Date()).toFormat(defaultDateFormat)

const t9nLabel = computed(() => i18n.t(props.label))

const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))

const refDateInput = computed({
  get() {
    if (dateInputValue.value) {
      return formateDateFromHHMM(dateInputValue.value).toJSDate()
    }
  },
  set(newValue) {
    dateInputValue.value = DateTime.fromJSDate(newValue).toFormat(defaultDateFormat)
  }
})
</script>

<style lang="scss" scoped></style>
